import React, { useEffect, useMemo, useState } from "react";

/**
 * Movies page
 * - Single input (max 200 chars) to add a movie title
 * - Reads from MOVIES_READ_URL (expects an array of strings or { title: string })
 * - Saves back the full list as JSON to MOVIES_WRITE_URL (same container, usually a SAS URL)
 * - Displays all titles in alphabetical order (case-insensitive)
 *
 * IMPORTANT: To enable saving to Azure Blob (or any static hosting), provide a writable URL
 * via a SAS token with write permissions and set MOVIES_WRITE_URL below.
 */

// READ from public blob (GET)
const MOVIES_READ_URL = "https://sopronpogo.blob.core.windows.net/pogo/movies.json";
// WRITE back to the same file (PUT). Provide a SAS URL with write perms (replace placeholder)
const MOVIES_WRITE_URL = "https://sopronpogo.blob.core.windows.net/pogo/movies.json?sp=rw&st=2025-10-20T02:46:51Z&se=2027-05-01T11:01:51Z&sv=2024-11-04&sr=b&sig=m%2B3FsemFuEbIezRUznafttLak2NMbgW0aXiiSj0MrC0%3D";


type Movie = string | { title: string };

function normalizeIn(items: Movie[]): string[] {
  const out: string[] = [];
  for (const it of items) {
    if (!it) continue;
    if (typeof it === "string") out.push(it);
    else if (typeof (it as any).title === "string") out.push((it as any).title);
  }
  return out;
}

function normalizeOut(items: string[]): string[] {
  // Save as a simple string array for compactness
  return items;
}

export default function Movies() {
  const [movies, setMovies] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(MOVIES_READ_URL, { headers: { Accept: "application/json" }, cache: "no-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as Movie[];
        const list = Array.isArray(json) ? normalizeIn(json) : [];
        if (alive) setMovies(list);
      } catch (e: any) {
        if (alive) setError(e?.message || "fetch_failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const cleanedInput = input.trim();
  const canSubmit = cleanedInput.length > 0 && cleanedInput.length <= 200 && !saving;

  const sorted = useMemo(() => {
    const src = movies ?? [];
    return [...src].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [movies]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMsg(null);
    if (!canSubmit) return;
    if (!MOVIES_WRITE_URL) {
      setSaveMsg("Saving disabled: MOVIES_WRITE_URL (SAS) is not configured.");
      return;
    }

    // Build the updated list (dedupe case-insensitively)
    const next = new Map<string, string>();
    for (const t of sorted) next.set(t.toLocaleLowerCase(), t);
    const title = cleanedInput;
    next.set(title.toLocaleLowerCase(), title);
    const finalList = Array.from(next.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    try {
      setSaving(true);
      setError(null);

      const body = JSON.stringify(normalizeOut(finalList), null, 2);
      const put = await fetch(MOVIES_WRITE_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          // Azure Blob requires x-ms-blob-type on first create; for overwrite with SAS, not strictly needed
          // but harmless to include when targeting block blobs.
          "x-ms-blob-type": "BlockBlob",
        },
        body,
      });
      if (!put.ok) throw new Error(`Save failed: HTTP ${put.status}`);

      // Update local state after successful save
      setMovies(finalList);
      setInput("");
      setSaveMsg("Saved!");
    } catch (e: any) {
      setSaveMsg(null);
      setError(e?.message || "save_failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-body max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold mb-2">Movies</h1>
      <p className="opacity-80 mb-4">Add a movie title (max 200 characters). The list below is kept alphabetical.</p>

      <form onSubmit={onSubmit} className="mb-6 flex gap-2 items-start">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 200))}
          maxLength={200}
          placeholder="Type a movie title…"
          className="flex-1 border rounded-lg px-3 py-2"
          aria-label="Movie title"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className={`px-4 py-2 rounded-lg text-black bg-[#ffcb05] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed`}
          title={MOVIES_WRITE_URL ? "Save" : "Saving disabled: missing MOVIES_WRITE_URL"}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </form>

      {cleanedInput.length > 200 && (
        <p className="text-red-600 mb-4" role="alert">Title is too long (max 200 chars).</p>
      )}
      {error && (
        <p className="text-red-600 mb-4" role="alert">{error}</p>
      )}
      {saveMsg && (
        <p className="text-green-700 mb-4">{saveMsg}</p>
      )}

      <div>
        <h2 className="text-xl font-medium mb-2">All Movies ({sorted.length})</h2>
        {loading ? (
          <p className="opacity-80">Loading…</p>
        ) : (
          <ul className="list-disc pl-5 space-y-1">
            {sorted.map((t) => (
              <li key={t} className="leading-6">{t}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
